import { defineStore } from 'pinia';
import { streamEmailSettings, setEmailAccounts } from '@/services/messages.js';

export const useEmailSettingsStore = defineStore('emailSettings', {
  state: () => ({
    firmId: null,
    userId: null,
    // multi-account model on firms/{firmId}/members/{userId}:
    //   emailAccounts: { "<emailAddress>": {provider, emailAddress, ...} }
    //   activeEmailAddress: the one account currently being synced/used
    emailAccounts: {},
    activeEmailAddress: null,
    settingsUnsubscribe: null,
    processing_status: 'init',
    timeframe: null,
    counter: 0,
    // background sync/processing status (drives the status pill UI)
    syncStatus: 'idle', // 'idle' | 'syncing' | 'error'
    syncedThisRun: 0,
    syncError: null,
    unprocessedCount: 0,
    syncNonce: 0, // bumped to wake the sync loop early (e.g. horizon changed)
  }),
  getters: {
    // settings of the ACTIVE account — existing consumers keep working
    settings: (state) =>
      (state.activeEmailAddress && state.emailAccounts[state.activeEmailAddress])
        ? state.emailAccounts[state.activeEmailAddress]
        : {},
    accounts: (state) => Object.values(state.emailAccounts),
    hasAccounts: (state) => Object.keys(state.emailAccounts).length > 0,
    ignoredSenders() { return this.settings.ignoredSenders ? this.settings.ignoredSenders : []; },
    newestProcessedTimestamp() { return this.settings.newestProcessedTimestamp ?? null; },
    oldestProcessedTimestamp() { return this.settings.oldestProcessedTimestamp ?? null; },
    processingPaused() { return !!this.settings.processingPaused; },
  },
  actions: {
    // merge `settings` into the active account and persist the whole map
    updateSettings(settings) {
      if (!this.activeEmailAddress) {
        console.warn('updateSettings called with no active email account', settings);
        return Promise.resolve();
      }
      console.log('updateSettings', settings);
      const emailAccounts = {
        ...this.emailAccounts,
        [this.activeEmailAddress]: { ...this.settings, ...settings },
      };
      return setEmailAccounts(this.firmId, this.userId, emailAccounts);
    },
    // add a newly connected account (or merge into an existing one) and make
    // it active — only one account is synced/used at a time
    upsertAccount(emailAddress, settings) {
      const emailAccounts = {
        ...this.emailAccounts,
        [emailAddress]: { ...this.emailAccounts[emailAddress], ...settings, emailAddress },
      };
      // update local state immediately — the sync loop reads the active
      // account right after sign-in, before the Firestore snapshot arrives
      this.emailAccounts = emailAccounts;
      this.activeEmailAddress = emailAddress;
      this.timeframe = emailAccounts[emailAddress].timeframe || null;
      return setEmailAccounts(this.firmId, this.userId, emailAccounts, emailAddress);
    },
    // switch the active account; the caller is responsible for re-initializing
    // the provider client and re-authenticating
    setActiveAccount(emailAddress) {
      this.activeEmailAddress = emailAddress;
      const account = this.emailAccounts[emailAddress] || {};
      this.timeframe = account.timeframe || null;
      this.syncStatus = 'idle';
      this.syncedThisRun = 0;
      this.syncError = null;
      this.unprocessedCount = 0;
      return setEmailAccounts(this.firmId, this.userId, this.emailAccounts, emailAddress);
    },
    updateIgnoreSenders(ignored_senders) {
      const fsIgnoredSenders = [...this.ignoredSenders];
      // add the new ignored senders to the list
      for (let i=0; i<ignored_senders.length; i++) {
        if (!fsIgnoredSenders.includes(ignored_senders[i])) {
          fsIgnoredSenders.push(ignored_senders[i]);
        }
      }
      return this.updateSettings({ignoredSenders: fsIgnoredSenders});
    },
    subscribeToSettings(firmId, userId) {
      if (!firmId || !userId) {
        throw new Error('firm and user required');
      }
      if (firmId != this.firmId || userId != this.userId) {
        this.firmId = firmId;
        this.userId = userId;
        if (this.settingsUnsubscribe && typeof this.settingsUnsubscribe === 'function') {
          this.settingsUnsubscribe();
        }
        console.log('subscribing to email settings', this.firmId, this.userId);
        this.settingsUnsubscribe = streamEmailSettings(this.firmId, this.userId, (snapshot) => {
          const data = snapshot.data() || {};
          if (data.emailAccounts && Object.keys(data.emailAccounts).length > 0) {
            this.emailAccounts = data.emailAccounts;
            this.activeEmailAddress = data.activeEmailAddress || Object.keys(data.emailAccounts)[0];
          } else if (data.emailSettings && data.emailSettings.emailAddress) {
            // legacy single-account shape (pre-migration): synthesize the
            // accounts map in memory; it is persisted on the next write or by
            // scripts/_migrate_multi_email.js
            const legacy = data.emailSettings;
            this.emailAccounts = { [legacy.emailAddress]: legacy };
            this.activeEmailAddress = legacy.emailAddress;
          } else {
            this.emailAccounts = {};
            this.activeEmailAddress = null;
          }
          // restore the persisted sync horizon (timeframe) across sessions
          if (this.settings.timeframe) {
            this.timeframe = this.settings.timeframe;
          }
        });
      }
    },
    startProcessing() {
      this.processing_status = 'started';
    },
    stopProcessing() {
      this.processing_status = 'stopped';
    },
    setTimeframe(timeframe) {
      console.log('setTimeframe', timeframe);
      this.timeframe = timeframe;
      // persist as the sync horizon setting, and wake the sync loop so a
      // widened horizon backfills promptly
      this.updateSettings({timeframe: timeframe});
      this.syncNonce++;
    },
    increaseCounter() {
      this.counter++;
    },
    resetCounter() {
      this.counter = 0;
    },
    setSyncStatus(status, syncedThisRun = null, error = null) {
      this.syncStatus = status;
      if (syncedThisRun !== null) {
        this.syncedThisRun = syncedThisRun;
      }
      this.syncError = error;
    },
    setUnprocessedCount(n) {
      this.unprocessedCount = n;
    },
    // Pause/resume only gates processing (matching -> Firestore writes);
    // background sync keeps running so the local cache stays fresh.
    pauseProcessing() {
      return this.updateSettings({processingPaused: true});
    },
    resumeProcessing() {
      const p = this.updateSettings({processingPaused: false});
      this.syncNonce++; // wake the loop to drain the backlog now
      return p;
    },
  },
});

